import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Plus, X, Trash2, Image as ImageIcon, Loader2, ChevronLeft, Edit3, Check,
  ExternalLink, Wallet, Package, Pencil, Sparkles,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PALETTE = ['#FFC8DD', '#A2D2FF', '#B9FBC0', '#FFE8A3', '#CDB4DB', '#FFB4A2', '#FDF0D5', '#E0BBE4'];
const EMOJIS = ['🛋️', '🛏️', '🛁', '🍽️', '🪴', '🎨', '💍', '🎉', '📚', '🏡', '✨', '🌸'];

function compressImage(file, cb) {
  const reader = new FileReader();
  reader.onload = () => {
    const img = new Image();
    img.onload = () => {
      const maxW = 1280;
      let w = img.width, h = img.height;
      if (w > maxW) { h = Math.round(h * (maxW / w)); w = maxW; }
      const c = document.createElement('canvas');
      c.width = w; c.height = h;
      c.getContext('2d').drawImage(img, 0, 0, w, h);
      let url = c.toDataURL('image/jpeg', 0.72);
      if (url.length > 2_400_000) url = c.toDataURL('image/jpeg', 0.55);
      cb(url);
    };
    img.onerror = () => cb(null);
    img.src = reader.result;
  };
  reader.onerror = () => cb(null);
  reader.readAsDataURL(file);
}

const fmtKr = (n) => (Number(n) || 0).toLocaleString('sv-SE', { maximumFractionDigits: 0 });

// ============== NEW PROJECT MODAL ==============
function NewProjectModal({ open, onClose, onCreated }) {
  const [name, setName] = useState('');
  const [emoji, setEmoji] = useState('🛋️');
  const [color, setColor] = useState(PALETTE[0]);
  const [description, setDescription] = useState('');
  const [targetBudget, setTargetBudget] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) { setName(''); setEmoji('🛋️'); setColor(PALETTE[0]); setDescription(''); setTargetBudget(''); }
  }, [open]);

  if (!open) return null;

  const save = async () => {
    const n = name.trim();
    if (!n) { toast.error('Ge projektet ett namn'); return; }
    setBusy(true);
    try {
      const tb = targetBudget === '' ? null : Number(targetBudget);
      const { data } = await axios.post(`${API}/projects/`, {
        name: n, emoji, color, description: description.trim() || null,
        target_budget: (tb !== null && !Number.isNaN(tb) && tb >= 0) ? tb : null,
      }, { withCredentials: true });
      toast.success('Projekt skapat ✨');
      onCreated?.(data.project);
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Kunde inte spara');
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-[70] flex items-start sm:items-center justify-center p-3 overflow-y-auto" onClick={onClose} data-testid="project-modal">
      <div onClick={(e) => e.stopPropagation()} className="neo-card bg-white p-4 w-full max-w-md space-y-3">
        <div className="flex justify-between items-center">
          <h3 className="font-['Fredoka'] font-bold text-lg">Nytt projekt ✨</h3>
          <button onClick={onClose}><X className="w-5 h-5" /></button>
        </div>

        <input
          value={name} onChange={(e) => setName(e.target.value)}
          placeholder="t.ex. Renovera vardagsrummet"
          className="neo-input w-full text-sm"
          data-testid="project-name"
        />

        <div>
          <p className="text-[10px] font-bold uppercase opacity-60 mb-1">Emoji</p>
          <div className="grid grid-cols-6 gap-1">
            {EMOJIS.map((e) => (
              <button key={e} type="button" onClick={() => setEmoji(e)}
                className={`text-xl p-2 rounded-lg border-2 ${emoji === e ? 'bg-[#FFE8F5] border-[#D06892]' : 'bg-white border-black/10'}`}
                data-testid={`project-emoji-${e}`}>
                {e}
              </button>
            ))}
          </div>
        </div>

        <div>
          <p className="text-[10px] font-bold uppercase opacity-60 mb-1">Färgtema</p>
          <div className="flex gap-1 flex-wrap">
            {PALETTE.map((c) => (
              <button key={c} type="button" onClick={() => setColor(c)}
                className={`w-9 h-9 rounded-full border-2 ${color === c ? 'border-black ring-2 ring-[#D06892]' : 'border-black/10'}`}
                style={{ background: c }} data-testid={`project-color-${c}`} />
            ))}
          </div>
        </div>

        <textarea
          value={description} onChange={(e) => setDescription(e.target.value)}
          placeholder="Beskrivning (valfri)" rows={2}
          className="neo-input w-full text-sm" data-testid="project-description"
        />

        <div>
          <p className="text-[10px] font-bold uppercase opacity-60 mb-1 flex items-center gap-1">
            <Wallet className="w-3 h-3" /> Målbudget (kr, valfritt)
          </p>
          <input type="number" inputMode="decimal" min="0" step="1"
            value={targetBudget} onChange={(e) => setTargetBudget(e.target.value)}
            placeholder="t.ex. 15000" className="neo-input w-full text-sm"
            data-testid="project-target-budget" />
        </div>

        <button onClick={save} disabled={busy} className="neo-btn w-full py-3 bg-[#B9FBC0] font-bold text-base" data-testid="project-save">
          {busy ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : 'Skapa projekt 💖'}
        </button>
      </div>
    </div>
  );
}

// ============== ADD ITEM MODAL ==============
function AddItemModal({ open, projectId, onClose, onAdded }) {
  const [title, setTitle] = useState('');
  const [image, setImage] = useState('');
  const [price, setPrice] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [url, setUrl] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    if (open) { setTitle(''); setImage(''); setPrice(''); setQuantity(1); setUrl(''); setNote(''); }
  }, [open]);

  if (!open) return null;

  const handleFile = (file) => {
    if (!file || !file.type.startsWith('image/')) return;
    setBusy(true);
    compressImage(file, (u) => { setImage(u || ''); setBusy(false); });
  };

  const save = async () => {
    const t = title.trim();
    if (!t) { toast.error('Ge artikeln en titel'); return; }
    setBusy(true);
    try {
      const priceNum = price === '' ? null : Number(price);
      await axios.post(`${API}/projects/${projectId}/items`, {
        title: t,
        image: image || null,
        price: (priceNum !== null && !Number.isNaN(priceNum) && priceNum >= 0) ? priceNum : null,
        quantity: Math.max(1, Number(quantity) || 1),
        url: url.trim() || null,
        note: note.trim() || null,
      }, { withCredentials: true });
      toast.success('Artikel tillagd ✨');
      onAdded?.();
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Kunde inte spara');
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-[70] flex items-start sm:items-center justify-center p-3 overflow-y-auto" onClick={onClose} data-testid="item-modal">
      <div onClick={(e) => e.stopPropagation()} className="neo-card bg-white p-4 w-full max-w-md space-y-3">
        <div className="flex justify-between items-center">
          <h3 className="font-['Fredoka'] font-bold text-lg">Ny artikel 🛒</h3>
          <button onClick={onClose}><X className="w-5 h-5" /></button>
        </div>

        <input
          value={title} onChange={(e) => setTitle(e.target.value)}
          placeholder="t.ex. POÄNG-fåtölj" className="neo-input w-full text-sm"
          data-testid="item-title"
        />

        {/* image */}
        {!image ? (
          <label className="neo-btn flex flex-col items-center justify-center gap-1 py-5 bg-[#FFE8F5] text-xs font-bold cursor-pointer border-dashed border-2 border-[#D06892]/40" data-testid="item-photo-add">
            {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <ImageIcon className="w-5 h-5" />}
            <span>{busy ? 'Bearbetar…' : 'Lägg till bild (valfritt)'}</span>
            <input ref={fileRef} type="file" accept="image/*" onChange={(e) => handleFile(e.target.files?.[0])} className="hidden" disabled={busy} data-testid="item-photo-input" />
          </label>
        ) : (
          <div className="relative">
            <img src={image} alt="preview" className="max-h-40 w-full object-cover rounded-lg" />
            <button onClick={() => setImage('')} className="absolute -top-2 -right-2 bg-white rounded-full border-2 border-red-300 p-1">
              <X className="w-3 h-3 text-red-500" />
            </button>
          </div>
        )}

        <div className="grid grid-cols-2 gap-2">
          <div>
            <p className="text-[10px] font-bold uppercase opacity-60 mb-1">Pris (kr)</p>
            <input type="number" inputMode="decimal" min="0" step="1"
              value={price} onChange={(e) => setPrice(e.target.value)}
              placeholder="0" className="neo-input w-full text-sm"
              data-testid="item-price" />
          </div>
          <div>
            <p className="text-[10px] font-bold uppercase opacity-60 mb-1">Antal</p>
            <input type="number" min="1" step="1"
              value={quantity} onChange={(e) => setQuantity(e.target.value)}
              className="neo-input w-full text-sm"
              data-testid="item-quantity" />
          </div>
        </div>

        <input
          value={url} onChange={(e) => setUrl(e.target.value)}
          placeholder="Länk till butik (valfritt)" className="neo-input w-full text-sm"
          data-testid="item-url"
        />
        <textarea
          value={note} onChange={(e) => setNote(e.target.value)}
          placeholder="Anteckning (valfri)" rows={2}
          className="neo-input w-full text-sm" data-testid="item-note"
        />

        <button onClick={save} disabled={busy} className="neo-btn w-full py-3 bg-[#B9FBC0] font-bold text-base" data-testid="item-save">
          {busy ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : 'Spara artikel 💖'}
        </button>
      </div>
    </div>
  );
}

// ============== ITEM CARD (IKEA catalog tile) ==============
function ItemCard({ item, projectId, onChanged, onDeleted }) {
  const [editing, setEditing] = useState(null); // 'title' | 'price' | 'quantity' | null
  const [draft, setDraft] = useState('');

  const lineTotal = (Number(item.price) || 0) * (Number(item.quantity) || 1);

  const startEdit = (field) => {
    setEditing(field);
    setDraft(item[field] != null ? String(item[field]) : '');
  };

  const saveEdit = async () => {
    const field = editing;
    setEditing(null);
    if (field === null) return;
    let payload = {};
    if (field === 'title') {
      const t = draft.trim();
      if (!t) return;
      payload.title = t;
    } else if (field === 'price') {
      const n = draft === '' ? null : Number(draft);
      if (n !== null && Number.isNaN(n)) return;
      payload.price = n;
    } else if (field === 'quantity') {
      const n = Math.max(1, parseInt(draft, 10) || 1);
      payload.quantity = n;
    }
    try {
      await axios.patch(`${API}/projects/${projectId}/items/${item.id}`, payload, { withCredentials: true });
      onChanged?.();
    } catch (e) { toast.error('Kunde inte spara'); }
  };

  const remove = async () => {
    if (!window.confirm(`Ta bort "${item.title}"?`)) return;
    try {
      await axios.delete(`${API}/projects/${projectId}/items/${item.id}`, { withCredentials: true });
      onDeleted?.();
    } catch (e) { toast.error('Kunde inte ta bort'); }
  };

  return (
    <div className="neo-card bg-white overflow-hidden flex flex-col" data-testid={`item-${item.id}`}>
      <div className="aspect-square bg-[#F5F0FA] flex items-center justify-center overflow-hidden">
        {item.image ? (
          <img src={item.image} alt={item.title} className="w-full h-full object-cover" />
        ) : (
          <Package className="w-12 h-12 opacity-30" />
        )}
      </div>
      <div className="p-2 flex-1 flex flex-col gap-1">
        {/* Title (inline edit) */}
        {editing === 'title' ? (
          <input autoFocus value={draft} onChange={(e) => setDraft(e.target.value)}
            onBlur={saveEdit} onKeyDown={(e) => e.key === 'Enter' && saveEdit()}
            className="neo-input text-xs font-bold py-1" data-testid={`item-title-input-${item.id}`} />
        ) : (
          <button onClick={() => startEdit('title')}
            className="text-xs font-bold text-left line-clamp-2 hover:bg-[#FFE8F5] rounded px-1"
            data-testid={`item-title-${item.id}`} title="Klicka för att ändra">
            {item.title}
          </button>
        )}

        {/* Price + quantity */}
        <div className="flex items-center gap-1 text-[11px]">
          {editing === 'price' ? (
            <input autoFocus type="number" inputMode="decimal" min="0" step="1"
              value={draft} onChange={(e) => setDraft(e.target.value)}
              onBlur={saveEdit} onKeyDown={(e) => e.key === 'Enter' && saveEdit()}
              className="neo-input text-[11px] py-0.5 w-16"
              data-testid={`item-price-input-${item.id}`} />
          ) : (
            <button onClick={() => startEdit('price')}
              className="font-bold text-[#D06892] hover:bg-[#FFE8F5] rounded px-1"
              data-testid={`item-price-${item.id}`}>
              {item.price != null ? `${fmtKr(item.price)} kr` : '— kr'}
            </button>
          )}
          <span className="opacity-50">×</span>
          {editing === 'quantity' ? (
            <input autoFocus type="number" min="1" step="1"
              value={draft} onChange={(e) => setDraft(e.target.value)}
              onBlur={saveEdit} onKeyDown={(e) => e.key === 'Enter' && saveEdit()}
              className="neo-input text-[11px] py-0.5 w-12"
              data-testid={`item-qty-input-${item.id}`} />
          ) : (
            <button onClick={() => startEdit('quantity')}
              className="hover:bg-[#FFE8F5] rounded px-1"
              data-testid={`item-qty-${item.id}`}>
              {item.quantity || 1}
            </button>
          )}
        </div>

        {/* Line total */}
        {item.price != null && (item.quantity || 1) > 1 && (
          <p className="text-[10px] opacity-60" data-testid={`item-line-${item.id}`}>
            = {fmtKr(lineTotal)} kr
          </p>
        )}

        {/* Footer actions */}
        <div className="flex items-center gap-2 mt-auto pt-1 border-t border-black/5 text-[10px]">
          {item.url && (
            <a href={item.url} target="_blank" rel="noreferrer" className="opacity-60 hover:opacity-100 flex items-center gap-1"
              data-testid={`item-link-${item.id}`}>
              <ExternalLink className="w-3 h-3" /> Länk
            </a>
          )}
          <button onClick={remove} className="opacity-40 hover:opacity-100 ml-auto"
            data-testid={`item-delete-${item.id}`}>
            <Trash2 className="w-3 h-3" />
          </button>
        </div>
      </div>
    </div>
  );
}

// ============== PROJECT DETAIL ==============
function ProjectDetail({ projectId, onBack }) {
  const [project, setProject] = useState(null);
  const [busy, setBusy] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [editingName, setEditingName] = useState(false);
  const [editingBudget, setEditingBudget] = useState(false);
  const [draft, setDraft] = useState('');

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const { data } = await axios.get(`${API}/projects/${projectId}`, { withCredentials: true });
      setProject(data.project);
    } catch (e) { toast.error('Kunde inte ladda projekt'); }
    finally { setBusy(false); }
  }, [projectId]);

  useEffect(() => { load(); }, [load]);

  const updateField = async (field, value) => {
    try {
      await axios.patch(`${API}/projects/${projectId}`, { [field]: value }, { withCredentials: true });
      load();
    } catch (e) { toast.error('Kunde inte spara'); }
  };

  if (busy || !project) {
    return (
      <div className="neo-card p-8 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto opacity-60" /></div>
    );
  }

  const total = Number(project.totals?.all) || 0;
  const target = Number(project.target_budget) || 0;
  const overBudget = target > 0 && total > target;
  const ratio = target > 0 ? Math.min(total / target, 2) : 0;

  return (
    <div className="space-y-3" data-testid={`project-detail-${projectId}`}>
      {/* Header */}
      <section className="neo-card p-4" style={{ background: project.color || '#FFC8DD' }}>
        <div className="flex items-center gap-2 mb-2">
          <button onClick={onBack} className="neo-btn px-3 py-2 bg-white text-sm font-bold flex items-center gap-1" data-testid="project-back">
            <ChevronLeft className="w-4 h-4" /> Tillbaka
          </button>
        </div>
        <div className="flex items-start gap-3">
          <span className="text-4xl">{project.emoji || '🛋️'}</span>
          <div className="flex-1">
            {editingName ? (
              <input autoFocus value={draft} onChange={(e) => setDraft(e.target.value)}
                onBlur={() => { if (draft.trim()) updateField('name', draft.trim()); setEditingName(false); }}
                onKeyDown={(e) => e.key === 'Enter' && e.currentTarget.blur()}
                className="neo-input font-['Fredoka'] font-bold text-xl py-1"
                data-testid="project-name-input" />
            ) : (
              <button onClick={() => { setEditingName(true); setDraft(project.name); }}
                className="font-['Fredoka'] font-bold text-2xl text-left"
                data-testid="project-name-display">
                {project.name}
              </button>
            )}
            {project.description && <p className="text-xs opacity-70 mt-0.5">{project.description}</p>}
          </div>
        </div>

        {/* Totals */}
        <div className="mt-3 grid grid-cols-2 gap-2">
          <div className="neo-card p-2 bg-white text-center">
            <p className="text-[10px] uppercase opacity-60 font-bold">Totalsumma</p>
            <p className="text-xl font-bold font-['Fredoka'] text-[#D06892]" data-testid="project-total">
              {fmtKr(total)} kr
            </p>
            <p className="text-[10px] opacity-50">{project.totals?.item_count || 0} artiklar</p>
          </div>
          <div className="neo-card p-2 bg-white text-center">
            <p className="text-[10px] uppercase opacity-60 font-bold flex items-center justify-center gap-1">
              <Wallet className="w-3 h-3" /> Målbudget
            </p>
            {editingBudget ? (
              <input autoFocus type="number" min="0" step="1" value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onBlur={() => { updateField('target_budget', draft === '' ? null : Number(draft)); setEditingBudget(false); }}
                onKeyDown={(e) => e.key === 'Enter' && e.currentTarget.blur()}
                className="neo-input text-base font-bold py-0.5 text-center"
                data-testid="project-budget-input" />
            ) : (
              <button onClick={() => { setEditingBudget(true); setDraft(target > 0 ? String(target) : ''); }}
                className="text-xl font-bold font-['Fredoka']"
                data-testid="project-budget-display">
                {target > 0 ? `${fmtKr(target)} kr` : 'Sätt mål'}
              </button>
            )}
          </div>
        </div>

        {/* Progress bar */}
        {target > 0 && (
          <div className="mt-2" data-testid="project-progress">
            <div className="w-full h-3 rounded-full border-2 border-black overflow-hidden bg-white">
              <div className="h-full transition-all"
                style={{
                  width: `${Math.min(ratio * 100, 200)}%`,
                  background: overBudget
                    ? 'linear-gradient(90deg,#FFB4A2,#FF6B6B)'
                    : 'linear-gradient(90deg,#B9FBC0,#A2D2FF)',
                }} />
            </div>
            <p className="text-[10px] opacity-80 mt-1 font-bold">
              {overBudget ? `⚠️ ${fmtKr(total - target)} kr över målbudgeten` : `🌸 ${fmtKr(target - total)} kr kvar`}
            </p>
          </div>
        )}
      </section>

      {/* Add item button */}
      <button
        onClick={() => setShowAdd(true)}
        className="neo-btn w-full py-3 bg-[#FFC8DD] font-bold flex items-center justify-center gap-2"
        data-testid="project-add-item-btn">
        <Plus className="w-4 h-4" /> Lägg till artikel
      </button>

      {/* Items grid (IKEA catalog) */}
      {(project.items || []).length === 0 ? (
        <div className="text-center py-10 opacity-60" data-testid="project-empty">
          <Package className="w-10 h-10 mx-auto mb-2" />
          <p className="text-sm">Inga artiklar än 🛋️</p>
          <p className="text-xs">Lägg till möbler, dekor eller vad du vill bygga</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2" data-testid="project-items-grid">
          {project.items.map((it) => (
            <ItemCard key={it.id} item={it} projectId={projectId} onChanged={load} onDeleted={load} />
          ))}
        </div>
      )}

      <AddItemModal open={showAdd} projectId={projectId} onClose={() => setShowAdd(false)} onAdded={load} />
    </div>
  );
}

// ============== MAIN PROJECTS TAB ==============
export default function ProjectsTab() {
  const [projects, setProjects] = useState([]);
  const [busy, setBusy] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [activeId, setActiveId] = useState(null);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const { data } = await axios.get(`${API}/projects/`, { withCredentials: true });
      setProjects(data.projects || []);
    } catch (e) { /* silent */ }
    finally { setBusy(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const remove = async (id) => {
    if (!window.confirm('Ta bort projektet och alla artiklar?')) return;
    try {
      await axios.delete(`${API}/projects/${id}`, { withCredentials: true });
      load();
    } catch (e) { toast.error('Kunde inte ta bort'); }
  };

  if (activeId) {
    return <ProjectDetail projectId={activeId} onBack={() => { setActiveId(null); load(); }} />;
  }

  return (
    <div className="space-y-3" data-testid="projects-tab">
      <header className="neo-card p-4 bg-gradient-to-br from-[#FFE8A3] via-[#FFC8DD] to-[#E0BBE4]">
        <p className="text-[10px] font-bold tracking-[0.25em] uppercase opacity-60">Projektkatalog</p>
        <h1 className="font-['Fredoka'] font-bold text-2xl mt-0.5">Mina projekt 🛋️</h1>
        <p className="text-xs opacity-70 mt-1">Bygg ihop drömmar med priser — direkt totalsumma som i en IKEA-katalog.</p>
        <button onClick={() => setShowNew(true)} className="neo-btn px-3 py-2 bg-white text-sm font-bold flex items-center gap-1 mt-3" data-testid="project-new-btn">
          <Plus className="w-4 h-4" /> Nytt projekt
        </button>
      </header>

      {busy ? (
        <div className="neo-card p-8 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto opacity-60" /></div>
      ) : projects.length === 0 ? (
        <div className="text-center py-12 opacity-70" data-testid="projects-empty">
          <Sparkles className="w-10 h-10 mx-auto mb-2 text-[#D06892]" />
          <p className="text-base font-bold">Inga projekt än</p>
          <p className="text-xs opacity-60 mb-3">Skapa ditt första projekt — som en IKEA-katalog för din dröm</p>
          <button onClick={() => setShowNew(true)} className="neo-btn px-4 py-2 bg-[#FFC8DD] font-bold text-sm" data-testid="projects-empty-cta">
            + Skapa projekt
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="projects-grid">
          {projects.map((p) => {
            const total = Number(p.totals?.all) || 0;
            const target = Number(p.target_budget) || 0;
            const over = target > 0 && total > target;
            const ratio = target > 0 ? Math.min(total / target, 1.5) : 0;
            return (
              <button
                key={p.id}
                onClick={() => setActiveId(p.id)}
                className="neo-card p-4 text-left relative hover:scale-[1.02] active:scale-95 transition-transform"
                style={{ background: p.color || '#FFC8DD' }}
                data-testid={`project-card-${p.id}`}>
                <div className="flex items-start gap-2">
                  <span className="text-3xl">{p.emoji || '🛋️'}</span>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-['Fredoka'] font-bold text-base truncate">{p.name}</h3>
                    {p.description && <p className="text-[10px] opacity-70 line-clamp-1">{p.description}</p>}
                  </div>
                  <span
                    role="button" tabIndex={0}
                    onClick={(e) => { e.stopPropagation(); remove(p.id); }}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); remove(p.id); } }}
                    className="text-xs opacity-40 hover:opacity-100 cursor-pointer p-1"
                    data-testid={`project-delete-${p.id}`}>
                    <Trash2 className="w-3 h-3" />
                  </span>
                </div>
                <div className="mt-3 flex items-end justify-between">
                  <div>
                    <p className="text-[10px] uppercase opacity-60 font-bold">Total</p>
                    <p className="text-lg font-bold font-['Fredoka']" data-testid={`project-total-${p.id}`}>
                      {fmtKr(total)} kr
                    </p>
                  </div>
                  <p className="text-[10px] opacity-60">📦 {p.totals?.item_count || 0} st</p>
                </div>
                {target > 0 && (
                  <div className="mt-2 w-full h-2 rounded-full border-2 border-black overflow-hidden bg-white/60">
                    <div className="h-full" style={{
                      width: `${Math.min(ratio * 100, 150)}%`,
                      background: over ? '#FF6B6B' : 'linear-gradient(90deg,#B9FBC0,#A2D2FF)',
                    }} />
                  </div>
                )}
                {target > 0 && (
                  <p className="text-[10px] opacity-70 mt-1">
                    av {fmtKr(target)} kr {over ? '⚠️' : '🌸'}
                  </p>
                )}
              </button>
            );
          })}
        </div>
      )}

      <NewProjectModal open={showNew} onClose={() => setShowNew(false)} onCreated={() => load()} />
    </div>
  );
}

// ============== ADD-TO-PROJECT PICKER (for InspirationTab) ==============
export function AddToProjectPicker({ pinId, open, onClose }) {
  const [projects, setProjects] = useState([]);
  const [busy, setBusy] = useState(false);
  const [creatingNew, setCreatingNew] = useState(false);
  const [newName, setNewName] = useState('');

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/projects/`, { withCredentials: true });
        setProjects(data.projects || []);
      } catch (e) { /* silent */ }
    })();
  }, [open]);

  if (!open) return null;

  const importPin = async (projectId) => {
    setBusy(true);
    try {
      await axios.post(`${API}/projects/${projectId}/import-pin/${pinId}`, {}, { withCredentials: true });
      toast.success('Tillagd i projektet ✨');
      onClose?.();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Kunde inte lägga till'); }
    finally { setBusy(false); }
  };

  const createAndImport = async () => {
    const n = newName.trim();
    if (!n) return;
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/projects/`, { name: n, emoji: '🛋️', color: '#FFC8DD' }, { withCredentials: true });
      await axios.post(`${API}/projects/${data.project.id}/import-pin/${pinId}`, {}, { withCredentials: true });
      toast.success(`Skapade "${n}" och la till pinnen ✨`);
      onClose?.();
    } catch (e) { toast.error('Kunde inte skapa projekt'); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-[80] flex items-center justify-center p-3" onClick={onClose} data-testid="add-to-project-picker">
      <div onClick={(e) => e.stopPropagation()} className="neo-card bg-white p-4 w-full max-w-sm space-y-2 max-h-[80vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-2">
          <h3 className="font-['Fredoka'] font-bold">Lägg till i projekt</h3>
          <button onClick={onClose}><X className="w-4 h-4" /></button>
        </div>

        {projects.map((p) => (
          <button key={p.id} onClick={() => importPin(p.id)} disabled={busy}
            className="w-full neo-btn p-2 text-left flex items-center gap-2"
            style={{ background: p.color || '#FFC8DD' }}
            data-testid={`pick-project-${p.id}`}>
            <span className="text-xl">{p.emoji || '🛋️'}</span>
            <span className="flex-1 font-bold text-sm">{p.name}</span>
            <span className="text-[10px] opacity-60">{fmtKr(p.totals?.all || 0)} kr</span>
          </button>
        ))}

        <div className="border-t border-black/10 pt-2 mt-2">
          {creatingNew ? (
            <div className="flex gap-1">
              <input autoFocus value={newName} onChange={(e) => setNewName(e.target.value)}
                placeholder="Nytt projektnamn" className="neo-input flex-1 text-sm"
                onKeyDown={(e) => e.key === 'Enter' && createAndImport()}
                data-testid="new-project-inline-name" />
              <button onClick={createAndImport} disabled={busy || !newName.trim()}
                className="neo-btn px-3 bg-[#B9FBC0] text-sm font-bold"
                data-testid="new-project-inline-save">
                <Check className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <button onClick={() => setCreatingNew(true)}
              className="w-full neo-btn p-2 text-sm font-bold flex items-center gap-2 bg-white"
              data-testid="new-project-inline-open">
              <Plus className="w-4 h-4" /> Skapa nytt projekt
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
